/**
 * Username validation utilities for MemeLaunch
 * 
 * Rules:
 * - Lowercase only (a-z, 0-9, underscore)
 * - Length: 3-16 characters
 * - No reserved route names
 * - Case-insensitive uniqueness (via username_lower column)
 */

// Reserved route names that cannot be used as usernames
export const RESERVED_USERNAMES = [
  'home',
  'create',
  'portfolio',
  'creator',
  'admin',
  'settings',
  'api',
  'search',
  'tokens',
  'token',
  'lists',
  'list',
  'feed',
  'following',
  'notifications',
  'fees',
  'about',
  'help',
  'support',
  'terms',
  'privacy',
  'memelaunch',
  'dextoro',
] as const;

// Username format regex: lowercase letters, numbers, underscore only
const USERNAME_REGEX = /^[a-z0-9_]{3,16}$/;

export interface UsernameValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate a username against format and reserved words
 */
export function validateUsername(username: string): UsernameValidationResult {
  // Trim and lowercase
  const normalized = username.trim().toLowerCase();

  // Check length
  if (normalized.length < 3) {
    return { isValid: false, error: 'Username must be at least 3 characters' };
  }
  if (normalized.length > 16) {
    return { isValid: false, error: 'Username must be 16 characters or less' };
  }

  // Check format (lowercase, numbers, underscore only)
  if (!USERNAME_REGEX.test(normalized)) {
    return { 
      isValid: false, 
      error: 'Username can only contain lowercase letters, numbers, and underscores' 
    };
  }

  // Check for leading/trailing underscores
  if (normalized.startsWith('_') || normalized.endsWith('_')) {
    return { 
      isValid: false, 
      error: 'Username cannot start or end with an underscore' 
    };
  }

  // Check for consecutive underscores
  if (normalized.includes('__')) {
    return { 
      isValid: false, 
      error: 'Username cannot have consecutive underscores' 
    };
  }

  // Check reserved words
  if (RESERVED_USERNAMES.includes(normalized as typeof RESERVED_USERNAMES[number])) {
    return { isValid: false, error: 'This username is reserved' };
  }

  return { isValid: true };
}

/**
 * Normalize a username (lowercase, trim)
 */
export function normalizeUsername(username: string): string {
  return username.trim().toLowerCase();
}

/**
 * Generate a random username based on a seed (wallet address)
 * Format: user_XXXX where XXXX is derived from the wallet address
 */
export function generateRandomUsername(seed: string): string {
  // Take characters from different parts of the seed for variety
  const hash = seed.split('').reduce((acc, char, idx) => {
    return (acc * 31 + char.charCodeAt(0) * (idx + 1)) >>> 0;
  }, 0);
  
  // Convert to base36 and take 6 characters
  const suffix = hash.toString(36).slice(0, 6);
  
  return `user_${suffix}`;
}

/**
 * Check if a username can be changed (24-hour cooldown)
 */
export function canChangeUsername(lastChangedAt: string | null): { 
  canChange: boolean; 
  hoursRemaining?: number;
} {
  if (!lastChangedAt) {
    return { canChange: true };
  }

  const lastChanged = new Date(lastChangedAt);
  const now = new Date();
  const hoursSinceLastChange = (now.getTime() - lastChanged.getTime()) / (1000 * 60 * 60);
  
  if (hoursSinceLastChange >= 24) {
    return { canChange: true };
  }

  return { 
    canChange: false, 
    hoursRemaining: Math.ceil(24 - hoursSinceLastChange) 
  };
}
